<template>
  <SidebarForm
    id="custom-timeslot-sidebar"
    formId="custom-timeslot-form"
    v-model="visible"
    :headerTitle="t('sites.stationAppConfig.preOrder.form.dropoff.custom.sidebar.header')"
    :primaryActionLabel="
      t('sites.stationAppConfig.preOrder.form.dropoff.custom.sidebar.actions.save')
    "
    :schema="schema"
    :initialValues="initialValues"
    @primaryAction="submit"
  >
    <template #content>
      <Grid>
        <Col12>
          <span class="form-subtext">{{
            t('sites.stationAppConfig.preOrder.form.dropoff.custom.sidebar.timeslotHeader')
          }}</span>
        </Col12>
        <Col6>
          <CalendarField
            name="start_time"
            :label="t('sites.singleSite.sidebar.form.startTime')"
            timeOnly
            :hourFormat="`12`"
            :required="true"
          />
        </Col6>
        <Col6>
          <CalendarField
            name="end_time"
            :label="t('sites.singleSite.sidebar.form.endTime')"
            timeOnly
            :hourFormat="`12`"
            :required="true"
          />
        </Col6>
        <Col12>
          <FieldValidator name="destinations">
            <template #default="{ value, handleChange, errorMessage, isInvalid }">
              <div :class="{ 'p-error': isInvalid }">
                <div class="flex p-field c-input-required mb-2">
                  <label for="custom-timeslot-spot-filter">{{
                    t(
                      'sites.stationAppConfig.preOrder.form.dropoff.custom.timeslots.displayCard.dropoffSpotsLabel',
                    )
                  }}</label>
                </div>
                <div class="mb-4">
                  <InputTextSearch
                    inputId="custom-timeslot-spot-filter"
                    v-model="searchQuery"
                    :placeholder="
                      t(
                        'sites.stationAppConfig.preOrder.form.dropoff.locations.locationsListSidebar.filterPlaceholder',
                      )
                    "
                  />
                </div>
                <span v-if="errorMessage" class="error caption">{{ errorMessage }}</span>
              </div>
              <div
                v-for="destination in filteredDestinations"
                :key="destination"
                class="flex items-center mt-3"
              >
                <Checkbox
                  :id="`custom-timeslot-spot-${destination}`"
                  :value="destination"
                  :modelValue="value ?? []"
                  class="mr-4"
                  :class="{ 'p-invalid': isInvalid }"
                  @update:modelValue="handleChange"
                />
                <label :for="`custom-timeslot-spot-${destination}`" class="m-0 font-normal">
                  {{ destination }}
                </label>
              </div>
            </template>
          </FieldValidator>
        </Col12>
      </Grid>
    </template>
  </SidebarForm>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { DateTime } from 'luxon';
import { array, date, object, string } from 'yup';
import { FieldValidator } from '@centric-os/components/src/composed/validators/field-validator';
import type { CustomTimeslot } from '../types';

/** A form for one window. Times are Dates while editing and HH:mm strings on the way out. */
const props = defineProps<{ timeslot: CustomTimeslot | null; destinations: string[] }>();
const emit = defineEmits<{ submit: [timeslot: CustomTimeslot] }>();
const visible = defineModel<boolean>({ default: false });
const { t } = useI18n();

const toDate = (time: string): Date => DateTime.fromFormat(time, 'HH:mm').toJSDate();
const toTime = (value: Date): string => DateTime.fromJSDate(value).toFormat('HH:mm');

const startOfDay = DateTime.local().startOf('day').toJSDate();
const initialValues = computed(() => ({
  start_time: props.timeslot ? toDate(props.timeslot.start_time) : startOfDay,
  end_time: props.timeslot ? toDate(props.timeslot.end_time) : startOfDay,
  destinations: props.timeslot?.destinations ?? [],
}));

const searchQuery = ref('');
const filteredDestinations = computed(() =>
  props.destinations.filter((name) => name.toLowerCase().includes(searchQuery.value.toLowerCase())),
);

const schema = object({
  start_time: date().nullable().required(t('errorMessages.required')),
  end_time: date().nullable().required(t('errorMessages.required')),
  destinations: array().of(string()).min(1, t('sites.customTimeslotEditor.errors.spotRequired')),
});

const submit = (values: { start_time: Date; end_time: Date; destinations: string[] }): void => {
  emit('submit', {
    start_time: toTime(values.start_time),
    end_time: toTime(values.end_time),
    destinations: values.destinations,
  });
  visible.value = false;
};
</script>
